import {
  APP_INITIALIZER,
  Inject,
  Injectable,
  Injector,
  DOCUMENT,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { TranslateService } from '@ngx-translate/core';
import { NavigationRegions } from '@trustedshops/tswp-carrier-b2b-contracts';
import {
  BootstrapperBase,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import { PermissionRequest } from '@trustedshops/tswp-core-authorization';
import {
  NavigationEntries,
  ChannelManagementRoutes,
  ChannelSelectionService,
  InjectionTokens as CHANNEL_MANAGEMENT_TOKENS,
} from '@trustedshops/tswp-plugin-channel-management-contracts';
import {
  NavigationEntries as SETTINGS_NAVIGATION_ENTRIES,
  Routes as SETTINGS_ROUTES,
} from '@trustedshops/tswp-plugin-settings-contracts';
import { ChannelSelectionServiceImpl } from '@trustedshops/tswp-plugin-channel-management-autostart';
import {
  TOKENS as COMPOSITION_TOKENS,
  DependencyContainer,
} from '@trustedshops/tswp-core-composition';
import { TOKENS, PluginRouter } from '@trustedshops/tswp-core-plugins';
import {
  NavigationItem,
  NavigationService,
  TOKENS as TOKENS_UI,
  UserInteractionType,
  VisibilityGuardFactoryRegistry,
  VisibilityGuards,
} from '@trustedshops/tswp-core-ui';
import {
  ProductFeatureService,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import { ChannelManagementComponent } from './channel-management.component';
import { SettingsBootstrapper } from '../../configuration/settings.bootstrapper';

/**
 * The old standalone plugin's "is this account unverified" signal (see
 * `active-plugins.service.ts`) — reproduced here because it skipped all of
 * its own registration for unverified/free accounts, and product decided
 * to keep that behaviour after the carrier fold-in.
 */
const FEAT_REDUCED_MAIN_NAVIGATION = 'FEAT_REDUCED_MAIN_NAVIGATION';

@Injectable()
export class ChannelManagementBootstrapper implements BootstrapperBase {
  //#region Public Constants
  public static readonly TYPE: string =
    '@trustedshops/tswp-carrier-b2b:ChannelManagementBootstrapper';
  //#endregion

  //#region Ctor
  public constructor(
    private readonly _injector: Injector,

    @Inject(DOCUMENT)
    private readonly _document: Document,

    @Inject(TOKENS.PluginRouter)
    private readonly _pluginRouter: PluginRouter,

    @Inject(TOKENS_UI.NavigationService)
    private readonly _navigationService: NavigationService,

    @Inject(TOKENS_UI.VisibilityGuardFactoryRegistry)
    private readonly _visibilityGuardFactoryRegistry: VisibilityGuardFactoryRegistry,

    @Inject(COMPOSITION_TOKENS.DependencyContainer)
    private readonly _dependencyContainer: DependencyContainer,

    @Inject(MASTERDATA_TOKENS.ProductFeatureService)
    private readonly _productFeatureService: ProductFeatureService,

    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService: LogService,

    private readonly _translateService: TranslateService,

    private readonly _settingsBootstrapper: SettingsBootstrapper
  ) {}
  //#endregion

  //#region Public Methods
  public async initialize(): Promise<void> {
    if (await this.isUnverifiedAccount()) {
      this._logService.debug(
        ChannelManagementBootstrapper.TYPE,
        `${FEAT_REDUCED_MAIN_NAVIGATION} present — skipping channel management registration`
      );
      return;
    }
    this._logService.debug(
      ChannelManagementBootstrapper.TYPE,
      `Initializing channel management ${ChannelManagementComponent.selector} web component, route, and navigation item`
    );

    this.registerWebComponent();
    // Guarantees the 'settings' route already exists before registerRoute()
    // nests channel-management under it — see SettingsBootstrapper.initialized.
    await this._settingsBootstrapper.initialized;
    this.registerRoute();
    this.registerNavigationItem();
    await this.registerChannelSelectionService();
  }
  //#endregion

  //#region Private Methods
  private async isUnverifiedAccount(): Promise<boolean> {
    try {
      return await this._productFeatureService.hasFeature({
        featureNames: FEAT_REDUCED_MAIN_NAVIGATION,
      });
    } catch (err) {
      this._logService.error(
        ChannelManagementBootstrapper.TYPE,
        err instanceof Error ? err : new Error(String(err)),
        'Failed to fetch PFM features — proceeding with registration'
      );
      return false;
    }
  }

  private registerWebComponent(): void {
    const customElement = createCustomElement(ChannelManagementComponent, {
      injector: this._injector,
    });

    this._document.defaultView.customElements.define(
      ChannelManagementComponent.selector,
      customElement
    );
  }

  private registerRoute(): void {
    this._pluginRouter.addRoutes(
      [
        {
          id: ChannelManagementRoutes.ChannelSettings,
          webComponent: ChannelManagementComponent.selector,
          path: 'channel-management',
        },
      ],
      SETTINGS_ROUTES.Settings
    );
  }

  private registerNavigationItem(): void {
    // PL-1313: Legacy implementation for etrusted non-admin users.
    const permissionRequests: PermissionRequest[][] = [
      [
        {
          action: 'channel-mng:read',
          resource: '*',
          resourceType: 'channel',
        },
      ],
    ];

    const navigationItem = {
      order: 150,
      id: NavigationEntries.ChannelSettings,
      label: 'carrier.channel-management.navigation-main',
      icon: 'helios-icon helios-icon-action-favorite-with-heart',
      userInteraction: this._navigationService
        .getUserInteractionFactory(UserInteractionType.NavigateToFirstChild)
        .create({
          childrenRegion: NavigationRegions.TertiaryMenu,
          parentNavigationItemId: NavigationEntries.ChannelSettings,
        }),
      region: NavigationRegions.SecondaryMenu,
      visibilityGuards: [
        this._visibilityGuardFactoryRegistry.createVisibilityGuard(
          VisibilityGuards.Permission,
          permissionRequests
        ),
      ],
    } as unknown as NavigationItem;

    this._translateService
      .get(navigationItem.label)
      .subscribe((translation) => {
        navigationItem.label = translation;
        // Not awaited: getItemById (inside registerChildNavigationItem) waits
        // indefinitely for the "Settings" item if it is not registered yet, and
        // this runs inside an APP_INITIALIZER — awaiting it here would hang the
        // whole carrier bootstrap for any account where tswp-plugin-settings
        // never autostarts (unverified/free accounts, in particular).
        void this._navigationService.registerChildNavigationItem(
          SETTINGS_NAVIGATION_ENTRIES.Settings,
          [navigationItem]
        );
      });
  }

  /**
   * Registers ChannelSelectionServiceImpl into the root DI container so
   * external plugins resolving InjectionTokens.ChannelSelectionService
   * (via their own PluginContext.serviceProvider, which falls back to this
   * root container on a miss) get a live instance, and starts it.
   */
  private async registerChannelSelectionService(): Promise<void> {
    this._dependencyContainer.registerTokenizedType(
      CHANNEL_MANAGEMENT_TOKENS.ChannelSelectionService,
      ChannelSelectionServiceImpl
    );

    const channelSelectionService =
      this._dependencyContainer.get<ChannelSelectionService>(
        CHANNEL_MANAGEMENT_TOKENS.ChannelSelectionService
      );

    await (channelSelectionService as ChannelSelectionServiceImpl).initialize();
  }
  //#endregion
}

export const ChannelManagementInitializer = [
  ChannelManagementBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: ChannelManagementBootstrapper) => {
      return () => initializer.initialize();
    },
    deps: [ChannelManagementBootstrapper],
  },
];
